// cSpell: ignore vendormanager idgw Fullname changepassword passwordreset
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationExtras, Params, Router, UrlSegment } from "@angular/router";
import { CommandService } from "@tylertech/commands-module";
import { DialogService } from "@tylertech/forge-angular";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor, LogAccess, LogAs, LogInvocation, LogLevel, LogReturnAs, ProjectAs } from "@tylertech/logging";
import { LOCATION, StringHelper, WINDOW } from "@tylertech/shared-module";
import { ExecutionResult, LoginViewModel, SettingLookupOption, Token, TylerIdModel } from "@tylertech/tyler-api-types";
import { BehaviorSubject, lastValueFrom, Observable, retry, throwError, timer } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { LoginDialogComponent } from "../components";
import { LoginView } from "../interfaces";
import { NavigationService } from "./navigation.service";
import { OpenIdService } from "./openIdService";
import { RouteInfoService } from "./routeInfo.service";
import { StorageService } from "./storageService";
import { SystemInfoService, SystemInfoStorageKeys } from "./systemInfo.service";
import { WalkMeService } from "./walkMe.service";

export enum UserType
{
    User = 0,
    Employee = 1,
    Vendor = 2,
}

export const enum SecurityStorageKey
{
    AccessToken = "access_token",
    Username = "username",
    WalkMeDisabled = "walkMeDisabled",
    AlternateGridLines = "alternateGridLines",
    Settings = "settings",
    SystemInfo = "SystemInfo",
    DesiredRoute = "desiredRoute",
    RemindMeLater = "pw_reminder",
    SearchParams = "searchParams",
    CachedTidInfo = "cachedTidInfo",
    TidToken = "tid_token",
}

export const enum SecurityUrls
{
    Login = "/api/common/security/login",
    Refresh = "/api/common/security/refresh",
    TylerId = "/api/common/security/tylerId",
    TylerIdSettings = "/api/common/security/tylerIdSettings",
}

export const enum SecurityRoute
{
    Radius = "radius",
    Login = "login",
    Signout = "signout",
    ChangePassword = "changepassword",
    PasswordReset = "passwordreset",
    MigrateTid = "migrateTID",
    PopupLoginCallback = "popupLoginCallback",
}


export const enum SecuritySearchQueryKey
{
    RedirectPath = "redirectPath",
    RedirectParams = "redirectParams",
    AuthenticationKey = "K"
}

export const enum ConnectionSettings
{
    ConnGroup = "DefaultConnectionGroup",
    Department = "DefaultDepartment"
}

export type RedirectParams = Partial<Record<SecuritySearchQueryKey, string>>;

interface TidInfo
{
    readonly authority?: string;
}

type SingularSettingValue = SettingLookupOption | string | number | boolean;
export type SettingValue = Array<SingularSettingValue> | SingularSettingValue;

interface ConsolidatedRouteState
{
    url: UrlSegment[];
    queryParams: Params;
}

@Injectable()
export class SecurityService
{
    private readonly window = inject<Window>(WINDOW);
    private readonly location = inject<Location>(LOCATION);
    private readonly httpClient = inject(HttpClient);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly forgeDialog = inject(DialogService);
    private readonly $shellService = inject(ShellService);
    private readonly $storageService = inject(StorageService);
    private readonly $openIdService = inject(OpenIdService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly $commandService = inject(CommandService);
    private readonly $walkMeService = inject(WalkMeService);

    private readonly log = getLoggerFor("SecurityService");
    private readonly principleChangeSubject = new BehaviorSubject<Readonly<LoginView>>(undefined);
    readonly principleChange: Observable<Readonly<LoginView>>;

    private refreshTokenPromise: Promise<string>;

    private _initializingPromise: Promise<void>;
    private _isInitialized: boolean;
    private _redirectWithStorage: boolean;
    private _principle: LoginView;
    private queryParams: Readonly<Record<string, string>>;

    constructor()
    {
        const activatedRoute = this.activatedRoute;

        this.principleChange = this.log.enabled
            ? this.principleChangeSubject.asObservable()
                .pipe(
                    tap((newPrinciple: LoginViewModel) =>
                    {
                        this.log.debug(`principleChange::tap`, { newPrinciple });
                    }))
            : this.principleChangeSubject.asObservable();

        activatedRoute.queryParams
            .subscribe((params: Params) =>
            {
                this.queryParams = { ...params };
            });
    }

    get isInitialized(): boolean
    {
        return this._isInitialized;
    }

    @LogAccess({ level: LogLevel.DEBUG, logGet: false })
    get principle(): LoginView
    {
        return this._principle;
    }

    private set principle(v: LoginView)
    {
        const changed = this._principle !== v;
        this._principle = v;
        if (!changed)
        {
            return;
        }

        this.$commandService.setCompactMode(this.principle?.Dense);
        this.principleChangeSubject.next(this.principle);
    }

    /** @deprecated - library/incode should be able to moved lower in our dep tree so core should just use the shellService */
    private get isEmbedded(): boolean
    {
        // TODO - library/incode should be accessible here, so we shouldn't accessing the special CefSharp objects here.
        return this.$shellService.isEmbedded();
    }

    get isProvisional(): boolean
    {
        if (!this.hasSetting("IsProvisionalDatabase"))
        {
            return false;
        }

        return this.Settings["IsProvisionalDatabase"] as boolean;
    }

    /**
     * Tests that we have a principle and it's token has not expired.
     */
    get isAuthenticated(): boolean
    {
        if (this.principle)
        {
            const expireDate: number = new Date(this.principle.Token.expires)
                .valueOf();

            // TODO(DO-4549) - is this test different than `this.jwtHelperService.isTokenExpired(jwt, 300)`
            return expireDate > Date.now()
                // ? what is this extra check for in embedded contexts?
                && (this.isEmbedded || !!this.Tenant);
        }

        return false;
    }

    get Token(): string
    {
        if (this.isEmbedded)
        {
            // TODO - library/incode should be accessible here, so we shouldn't accessing the special CefSharp objects here.
            return this.$shellService.getAccessToken();
        }

        const token = this.FullToken?.access_token;
        return token || null;
    }

    /** @returns the Token value from the currently stored LoginView. */
    protected get FullToken(): Token
    {
        if (this.isEmbedded)
        {
            return null;
        }

        const fullToken = this.get<LoginView>(SecurityStorageKey.AccessToken)?.Token;
        return fullToken || null;
    }

    protected set FullToken(value: Token)
    {
        if (this.isEmbedded)
        {
            return;
        }

        const token = this.get<LoginView>(SecurityStorageKey.AccessToken);
        if (token)
        {
            token.Token = value;
            this.store(SecurityStorageKey.AccessToken, token);
        }
    }

    private get Tenant(): string
    {
        if (this.principle?.Tenant)
        {
            return this.principle.Tenant;
        }

        // TODO - if we don't have a principle or the principle doesn't have a Tenant, why would checking storage change anything?????
        return this.get<LoginView>(SecurityStorageKey.AccessToken)?.Tenant;
    }

    get Settings(): Readonly<Record<string, SettingValue>>
    {
        if (this.principle?.Settings)
        {
            return this.principle.Settings;
        }
        // TODO - if we don't have a principle or the principle doesn't have a Settings, why would checking storage change anything?????
        return this.get<Record<string, SettingValue>>(SecurityStorageKey.Settings);
    }

    get Username(): string
    {
        if (this.principle?.Username)
        {
            return this.principle.Domain
                ? `${this.principle.Domain}\\${this.principle.Username}`
                : this.principle.Username;
        }

        // TODO - if we don't have a principle or the principle doesn't have a Username, why would checking storage change anything?????
        return this.get<string>(SecurityStorageKey.Username);
    }

    get Type(): UserType
    {
        if (this.principle?.UserType)
        {
            return this.principle.UserType;
        }

        // TODO - if we don't have a principle or the principle doesn't have a UserType, why would checking storage change anything?????
        const token = this.get<LoginView>(SecurityStorageKey.AccessToken);
        return token?.UserType;
    }

    get IsAdministrator(): boolean
    {
        if (this.principle?.IsAdministrator)
        {
            return this.principle.IsAdministrator;
        }

        // TODO - if we don't have a principle or the principle doesn't have a IsAdministrator, why would checking storage change anything?????
        return this.get<LoginView>(SecurityStorageKey.AccessToken).IsAdministrator;
    }

    get IsCompactMode(): boolean
    {
        if (this.principle?.Dense)
        {
            return this.principle.Dense;
        }

        return this.get<LoginView>(SecurityStorageKey.AccessToken).Dense;
    }

    get UseTenKeyPlusAsTab(): boolean
    {
        if (this.principle?.TenKeyPlusAsTab)
        {
            return this.principle.TenKeyPlusAsTab;
        }

        return this.get<LoginView>(SecurityStorageKey.AccessToken).TenKeyPlusAsTab;
    }

    get UseAlternatingGridLines(): boolean
    {
        if (this.principle?.AlternateGridLines)
        {
            return this.principle.AlternateGridLines;
        }

        return this.get<LoginView>(SecurityStorageKey.AccessToken).AlternateGridLines;
    }

    get isVendorUser(): boolean
    {
        return this.Type === UserType.Vendor;
    }

    /**
     * This is used for tid-w only
     */
    get redirectWithStorage(): boolean { return this._redirectWithStorage; }
    protected set redirectWithStorage(value: boolean)
    {
        this._redirectWithStorage = value;
    }

    private cachedPWReminderIsValid(): boolean
    {
        const reminder = this.get<number>(SecurityStorageKey.RemindMeLater);
        // TODO - why are we comparing a number to a Date, is this actually doing what we expect?
        return !!reminder && reminder >= new Date(this.FullToken.expires)
            .valueOf();
    }

    /**
     * Tests if the current route is one of the given `SecurityRoute`s
     */
    // @LogInvocation(LogLevel.DEBUG, true)
    isRoute(candidates: ReadonlyArray<SecurityRoute>, snapshot?: ActivatedRouteSnapshot): boolean
    {
        console.assert(snapshot !== this.activatedRoute.snapshot, "ASSERTION FAILED: The given snapshot and the activatedRoute's are the same.");
        const path = (snapshot ?? this.activatedRoute.snapshot).routeConfig?.path;

        return candidates.includes(path as SecurityRoute);
        // return this.activatedRoute.snapshot.url.some(part => candidates.includes(part.path));
    }

    /**
    * This should only be called by our APP_INITIALIZER function.
    * @internal
    */
    @LogInvocation(LogLevel.DEBUG)
    initialize(): Promise<void>
    {
        if (this._isInitialized)
        {
            throw new Error("SecurityService cannot be initialized more than once.");
        }

        // TODO(DO-4549) - Should this be an APP_INITIALIZER INSTEAD?
        if (this.isEmbedded)
        {
            const shellLoginModel: LoginView = JSON.parse(this.$shellService.getLoginViewModel());

            // TODO - why isn't the ShellAccessProvider setting the expires on the token it gives us?
            shellLoginModel.Token.expires = new Date(new Date()
                .getTime() + 24 * 60 * 60 * 1000)
                .toString();

            this.store<LoginView>(SecurityStorageKey.AccessToken, shellLoginModel);

            /*
             * TODO - Shouldn't we be able to just return here?
             * Why do we need to continue with detecting TylerId settings, and testing the token? Shouldn't that be the
             * responsibility of the Host that we are embedded in?
             */
        }

        this._initializingPromise = this.requireTylerId()
            .then(
                (/* _isTylerIdLogin: boolean */) =>
                {
                    const loginModel = this.get<LoginView>(SecurityStorageKey.AccessToken);
                    if (!loginModel?.Token)
                    {
                        return;
                    }

                    // TODO - should be calling `this.updateTokenInfo`?
                    this.log.information("Using token from local storage");
                    this.store<LoginView>(SecurityStorageKey.AccessToken, loginModel);
                    this.principle = loginModel;

                    this.$walkMeService.setVariables(this.principle);
                },
                e =>
                {
                    return this.log.error("Error while determining isTylerIdLogin", e);
                })
            .finally(() =>
            {
                this._initializingPromise = undefined;
                this._isInitialized = true;
            });

        return this._initializingPromise;
    }

    @LogInvocation(LogLevel.DEBUG)
    refreshToken(): Promise<string>
    {
        if (!this.refreshTokenPromise)
        {
            this.refreshTokenPromise = lastValueFrom(this.requestTokenRefresh());
        }

        return this.refreshTokenPromise;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    @LogReturnAs((_: string) => `[string Token]`)
    private requestTokenRefresh(): Observable<string>
    {
        const token = this.FullToken;

        /* eslint-disable @typescript-eslint/naming-convention */
        const headers = new HttpHeaders({
            "Content-Type": "application/json"
        });
        /* eslint-enable @typescript-eslint/naming-convention */
        return this.httpClient.post<Token>(
            SecurityUrls.Refresh,
            token,
            {
                headers,
                observe: "response",
                responseType: 'json',
            })
            .pipe(
                retry({
                    count: 1,
                    delay: (error: any) =>
                    {
                        this.log.error("Could not refresh token.", { error });
                        if (error instanceof HttpErrorResponse)
                        {
                            // Only retry if it's a connection/network error
                            // 0: connection closed unexpectedly/unknown
                            // 408: Request Timeout (mostly due to client's poor network conditions)
                            // 409: Conflict (mostly DB concurrency errors, so maybe give it another chance)
                            // 502: Bad Gateway (maybe a temporary network issue)
                            // 503: Service Unavailable (maybe a temporary server issue)
                            // 504: Gateway Timeout (maybe load balancer cannot talk to express temporarily)
                            if (error.status === 0 || error.status === 408 || error.status === 409 ||
                                error.status === 502 || error.status === 503 || error.status === 504)
                            {
                                return timer(1000);
                            }
                        }

                        // For 500 etc. pass the error down the pipe immediately
                        return throwError(() => error);
                    }
                }),
                map((response: HttpResponse<Token>) =>
                {
                    this.FullToken = response.body;

                    return this.Token;
                }),
                catchError((error) =>
                {
                    return this.promptForLogin();
                }),
                finalize(() =>
                {
                    this.refreshTokenPromise = undefined;
                })
            );
    }

    @LogInvocation(LogLevel.DEBUG, true)
    protected promptForLogin(): Observable<string>
    {
        const dialogRef = this.forgeDialog.open(LoginDialogComponent, {
            options: {
                mode: "modal",
            },
        });

        return dialogRef.afterClosed
            .pipe(
                map(loginSucceeded =>
                {
                    if (!loginSucceeded)
                    {
                        throw new Error("Login failed");
                    }

                    return this.Token;
                }));
    }

    // TODO(DO-4549) - this should live on the UserService?
    hasPersona(persona: string): boolean
    {
        if (!this.principle?.UserPersona)
        {
            return persona === "General User";
        }

        // FIXME - FALSE NEGATIVE: Will return false if the user's persona begins with the requested persona
        return this.principle.UserPersona.indexOf(persona) >= 0;
    }

    @LogInvocation(LogLevel.DEBUG, true)
    hasPrivilege(privilege: string): boolean
    {
        if (this.IsAdministrator)
        {
            return true;
        }

        if (this.principle?.Privileges)
        {
            return this.principle.Privileges.indexOf(privilege) > -1;
        }

        return this.get<LoginView>(SecurityStorageKey.AccessToken).Privileges.indexOf(privilege) > -1;
    }

    // TODO(DO-4549) - this should live on the UserService?
    hasSetting(key: string): boolean
    {
        if (!this.Settings || !this.Settings[key])
        {
            return false;
        }
        return true;
    }

    /**
     * @deprecated - what is this doing?
     * @returns
     */
    @LogInvocation(LogLevel.DEBUG)
    isNewToken(): boolean
    {
        if (this.cachedPWReminderIsValid()
            || this.isEmbedded)
        {
            return false;
        }

        this.store(
            SecurityStorageKey.RemindMeLater,
            new Date(this.FullToken.expires).valueOf());
        return true;
    }

    // TODO(DO-4549) - give this a better name...
    @LogInvocation(LogLevel.DEBUG)
    private passwordResetRequired(key: string): void
    {
        this.router.navigate([SecurityRoute.PasswordReset], <NavigationExtras>{
            queryParams: {
                key: key
            }
        });
    }

    /** @deprecated - should this just be moved into the navigation service? */
    @LogInvocation(LogLevel.DEBUG)
    private redirectRoutingForVendor(): void
    {
        if (!this.$navigationService.isApp("vendormanager"))
        {
            this.$navigationService.navigateToApp("vendormanager");
        }
    }

    protected requireTylerId(): Promise<void>
    {
        // TODO(DO-7967) - this doesn't need to be a promise. our APP_INITIALIZER ensures that systeminfo is fetched before any other providers are instantiated.
        return this.$systemInfoService.GetCachedSystemInfo()
            .then(x =>
            {
                // TODO - what are we doing here? Why is this string special
                const tidAuthorityStr = "idgw";
                if (x.useTylerId
                    && this.get<TidInfo>(SecurityStorageKey.CachedTidInfo)?.authority?.indexOf(tidAuthorityStr) < 0)
                {
                    this.redirectWithStorage = true;
                }
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    private returnToDesiredRoute(): void
    {
        if (this.redirectWithStorage)
        {
            const desiredRoute = this.get<Array<string>>(SecurityStorageKey.DesiredRoute);
            this.forget(SecurityStorageKey.DesiredRoute);

            const searchParams = this.get<string>(SecurityStorageKey.SearchParams);
            this.forget(SecurityStorageKey.SearchParams);

            const validRoute = Array.isArray(desiredRoute) && desiredRoute.length > 0;

            this.router.navigate(
                validRoute ? desiredRoute : [''],
                {
                    queryParams: searchParams ? JSON.parse(searchParams) : undefined,
                });

            return;
        }

        const redirectPath = this.queryParams.redirectPath;
        const redirectParams = this.queryParams.redirectParams
            ? JSON.parse(this.queryParams.redirectParams)
            : {};

        this.log.debug(`Redirecting to redirectPath or root.`,
            {
                redirectPath,
                redirectParams
            });
        this.router.navigate(
            redirectPath?.split("/") || [""],
            {
                queryParams: redirectParams,
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    private saveCredentials(credentials: LoginView): void
    {
        if (credentials.SaveCredentials === true)
        {
            const username = credentials.Domain
                ? `${credentials.Domain}\\${credentials.Username}`
                : credentials.Username;
            this.store<string>(SecurityStorageKey.Username, username);
        }
        else
        {
            this.forget(SecurityStorageKey.Username);
        }
    }

    /**
     * Why are we updating the settings store attached to the principle without updating the back end?
     * Why are we allowing the principle to be set outside of the login process?
     */
    @LogInvocation(LogLevel.DEBUG)
    updateSettings(model: LoginView): void
    {
        if (!model)
        {
            return;
        }

        let loginInfo = this.principle;
        if (!loginInfo)
        {
            // TODO - this seems super suspect.... why would we let some external caller set the principle to just any object?
            loginInfo = model;
        }
        else if (model.Token)
        {
            loginInfo.Token = model.Token;
        }

        if ("UserType" in model)
        {
            loginInfo.UserType = model.UserType;
        }

        if ("Dense" in model)
        {
            loginInfo.Dense = model.Dense;
        }

        if ("RowsPerPage" in model)
        {
            loginInfo.RowsPerPage = model.RowsPerPage;
        }

        if ("TenKeyPlusAsTab" in model)
        {
            loginInfo.TenKeyPlusAsTab = model.TenKeyPlusAsTab;
        }

        if ("DisableWalkMe" in model)
        {
            loginInfo.DisableWalkMe = model.DisableWalkMe;
            this.store<boolean>(SecurityStorageKey.WalkMeDisabled, loginInfo.DisableWalkMe);
        }

        if ("AlternateGridLines" in model)
        {
            loginInfo.AlternateGridLines = model.AlternateGridLines;
        }

        if ("Appearance" in model)
        {
            loginInfo.Appearance = model.Appearance;
        }

        if (!loginInfo.Username)
        {
            if (model.Username)
            {
                loginInfo.Username = model.Username;
            }
            else if (model.UserId)
            {
                loginInfo.Username = model.UserId;
            }
        }

        loginInfo.Settings = model.Settings;

        this.principle = loginInfo;
        this.store<LoginView>(SecurityStorageKey.AccessToken, loginInfo);
    }

    /** Why are we updating the settings store attached to the principle without updating the back end? */
    @LogInvocation(LogLevel.DEBUG)
    updateSingleSetting<T = unknown>(key: string, value: T): void
    {
        if (!key)
        {
            return;
        }

        this.principle.Settings[key] = value;
    }

    @LogInvocation(LogLevel.DEBUG)
    private updateTokenInfo(model: LoginView): void
    {
        if (!model)
        {
            return;
        }

        this.saveCredentials(model);
        // TODO - why are we trying to preserve the stored object?
        // TODO - this is only called after a login has be been authenticated (either system or TylerID), why would we have a stored value? /signout or being logged off should clear the stored principle?
        let loginInfo = this.get<LoginView>(SecurityStorageKey.AccessToken);
        if (!loginInfo)
        {
            loginInfo = model;
        }
        else if (model.Token)
        {
            loginInfo.Token = model.Token;
        }

        if (model.UserType)
        {
            loginInfo.UserType = model.UserType;
        }

        loginInfo.Privileges = model.Privileges;
        loginInfo.Email = model.Email;
        loginInfo.Fullname = model.Fullname;
        loginInfo.Tenant = model.Tenant;
        loginInfo.Username = model.Username;
        loginInfo.IsAdministrator = model.IsAdministrator;
        loginInfo.Settings = model.Settings;

        if (!this.window.sessionStorage.getItem("erp-conngroupid")
            && loginInfo.Settings
            && loginInfo.Settings[ConnectionSettings.ConnGroup])
        {
            this.window.sessionStorage.setItem("erp-conngroupid", loginInfo.Settings[ConnectionSettings.ConnGroup]["Value"]);
        }
        else if (this.window.sessionStorage.getItem("erp-conngroupid")
            && (!loginInfo.Settings
                || !loginInfo.Settings[ConnectionSettings.ConnGroup]))
        {
            this.window.sessionStorage.removeItem("erp-conngroupid");
        }

        this.store<LoginView>(SecurityStorageKey.AccessToken, loginInfo);

        this.principle = loginInfo;

        this.$walkMeService.setVariables(this.principle);

        this.$openIdService.clearToken();
        // ERP Pro App and ERP Pro app still need this
        const cefSharp = window["CefSharp"];
        if (cefSharp)
        {
            cefSharp.PostMessage(loginInfo);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    private updateTylerIdTokenInfo(token: string): void
    {
        this.store<string>(SecurityStorageKey.TidToken, token);
    }

    @LogInvocation(LogLevel.DEBUG, true)
    getTokenAfterTylerIdAuth(): Observable<boolean>
    {
        const tylerIdModel: TylerIdModel = {
            Token: this.$openIdService.getToken(),
            Tenant: this.$routeInfoService.tenant,
        };

        //test any changes to this in the shell and web launcher
        this.$shellService.PostMessage(tylerIdModel);

        const requestObservable = this.httpClient.post<ExecutionResult<LoginView>>(
            SecurityUrls.TylerId,
            tylerIdModel,
            {
                observe: "response",
                responseType: 'json',
            });

        return requestObservable
            .pipe(
                map(response =>
                {
                    const result = response.body;
                    if (!result.Success)
                    {
                        const loginError = new LoginError(response);
                        this.log.error(loginError);
                        throw loginError;
                    }

                    const loginViewModel = result.Result;
                    this.updateTokenInfo(loginViewModel);
                    this.updateTylerIdTokenInfo(tylerIdModel.Token);
                    return true;
                }));
    }


    @LogInvocation(LogLevel.DEBUG, true)
    authenticateTylerId(): Observable<boolean>
    {
        const tylerIdModel: TylerIdModel = {
            Token: this.$openIdService.getToken(),
            Tenant: this.$routeInfoService.tenant,
        };

        //test any changes to this in the shell and web launcher
        this.$shellService.PostMessage(tylerIdModel);

        const requestObservable = this.httpClient.post<ExecutionResult<LoginView>>(
            SecurityUrls.TylerId,
            tylerIdModel,
            {
                observe: "response",
                responseType: 'json',
            });

        return requestObservable
            .pipe(
                map(response =>
                {
                    const result = response.body;

                    // TODO(DO-4549) - The final router.navigate in this process should be completely resetting all the queryParams...
                    // this.$openIdService.clearParams();

                    if (!result.Success)
                    {
                        if (result.ErrorMessage.includes("error_description")
                            && result.ErrorMessage.includes("Token Is Expired"))
                        {
                            this.$openIdService.clearToken();
                            this.$openIdService.login();
                            return false;
                        }

                        this.log.error(new LoginError(response));
                        return false;
                    }

                    const loginViewModel = result.Result;
                    if (loginViewModel.MigrateTidUser)
                    {
                        this.updateTylerIdTokenInfo(tylerIdModel.Token);
                        this.router.navigate(["migrateTID"]);
                        return true;
                    }

                    if (!loginViewModel.RequiredPasswordChangeKey)
                    {
                        loginViewModel.SaveCredentials = false;

                        this.updateTokenInfo(loginViewModel);
                        this.updateTylerIdTokenInfo(tylerIdModel.Token);
                    }

                    this.redirectAfterLogin(loginViewModel);
                    return true;
                },));
    }

    @LogInvocation(LogLevel.DEBUG, true)
    login(
        @LogAs((c: LoginView) => `[LoginView "${c.Username}"]`)
        @ProjectAs((c: LoginView) => { return { ...c, Password: "********" } as LoginView; })
        credentials: LoginView): Observable<LoginView>
    {
        const isSavingCredentials = credentials.SaveCredentials || false;
        const requestObservable = this.httpClient.post<ExecutionResult<LoginView>>(
            SecurityUrls.Login,
            credentials,
            {
                observe: "response",
                responseType: 'json',
            });

        return requestObservable.pipe(
            map(
                (response: HttpResponse<ExecutionResult<LoginView>>) =>
                {
                    const result = response.body;
                    if (!result.Success)
                    {
                        throw new LoginError(response);
                    }

                    const loginViewModel = result.Result;
                    if (!loginViewModel.RequiredPasswordChangeKey)
                    {
                        result.Result.SaveCredentials = isSavingCredentials;

                        this.updateTokenInfo(result.Result);
                    }

                    return loginViewModel;
                }));
    }

    @LogInvocation(LogLevel.DEBUG)
    loginWithRedirect(
        @LogAs((c: LoginView) => `[LoginView "${c.Username}"]`)
        @ProjectAs((c: LoginView) => { return { ...c, Password: "********" } as LoginView; })
        credentials: LoginView): Observable<boolean>
    {
        return this.login(credentials)
            .pipe(
                map((result: LoginView) =>
                {
                    this.redirectAfterLogin(result);

                    return true;
                }),);
    }

    @LogInvocation(LogLevel.DEBUG)
    buildRedirectParams(routeSnapshot: ActivatedRouteSnapshot): RedirectParams
    {
        const consolidatedRouteInfo = this.consolidateRouteParams(routeSnapshot);
        if (this.isRedirectParams(consolidatedRouteInfo.queryParams))
        {
            return consolidatedRouteInfo.queryParams;
        }

        const routePath = consolidatedRouteInfo.url.map(x => x.path);
        const queryParams = StringHelper.stringify(consolidatedRouteInfo.queryParams ?? {},
            {
                propertiesToIgnore: [SecuritySearchQueryKey.RedirectPath, SecuritySearchQueryKey.RedirectParams, SecuritySearchQueryKey.AuthenticationKey]
            });
        const redirectParams = queryParams !== "{}"
            ? queryParams
            : undefined;

        if (this.redirectWithStorage)
        {
            this.store(SecurityStorageKey.DesiredRoute, routePath);
            if (redirectParams)
            {
                this.store(SecurityStorageKey.SearchParams, redirectParams);
            }

            const params: RedirectParams = {
                [SecuritySearchQueryKey.AuthenticationKey]: consolidatedRouteInfo.queryParams.K || undefined
            };

            return params;
        }

        const params: RedirectParams = {
            // Note: Forcing empty strings to be undefined for both params
            [SecuritySearchQueryKey.RedirectPath]: routePath.join("/") || undefined,
            [SecuritySearchQueryKey.RedirectParams]: redirectParams
        };

        if (consolidatedRouteInfo.queryParams && consolidatedRouteInfo.queryParams.K)
        {
            params.K = consolidatedRouteInfo.queryParams.K;
        }

        return params;
    }

    private isRedirectParams(params: Params): params is RedirectParams
    {
        if (!params)
        {
            return false;
        }

        const keys = Object.keys(params);
        switch (keys.length)
        {
            case 1:
                return keys[0] === SecuritySearchQueryKey.RedirectPath
                    || keys[0] === SecuritySearchQueryKey.RedirectParams;
            case 2:
                return keys.includes(SecuritySearchQueryKey.RedirectPath)
                    && keys.includes(SecuritySearchQueryKey.RedirectParams);
            case 3:
                return keys.includes(SecuritySearchQueryKey.RedirectPath)
                    && keys.includes(SecuritySearchQueryKey.RedirectParams)
                    && keys.includes(SecuritySearchQueryKey.AuthenticationKey);
            default:
                return false;
        }
    }

    /**
     * attempts to consolidate the url and query parameters from the given Router snapshot and it's children.
     */
    private consolidateRouteParams(snapshot: ActivatedRouteSnapshot): Readonly<ConsolidatedRouteState>
    {
        const consolidated: ConsolidatedRouteState = {
            url: [],
            queryParams: {}
        };

        return this.consolidateRouteParamsImpl(consolidated, snapshot);
    }

    @LogInvocation(LogLevel.DEBUG)
    private consolidateRouteParamsImpl(consolidated: ConsolidatedRouteState, snapshot: ActivatedRouteSnapshot, depth: number = 0): Readonly<ConsolidatedRouteState>
    {
        const self = this;
        if (consolidated.url.length === 0)
        {
            consolidated.url = snapshot.url ?? [];
        }

        if (snapshot.queryParams)
        {
            Object.entries(snapshot.queryParams)
                .forEach(addToConsolidate(consolidated.queryParams));
        }

        snapshot.children?.forEach(
            (childSnapshot) =>
            {
                this.consolidateRouteParamsImpl(consolidated, childSnapshot, depth + 1);
            });

        return consolidated;

        function addToConsolidate(destination: Params): ([name, value]: [string, string]) => void
        {
            return ([name, value]: [string, string]) =>
            {
                if (name in destination && destination[name] !== value)
                {
                    self.log.warning("consolidateRouteParams | naming collision", { destination: { ...destination }, name, value });
                }
                self.log.debug("consolidateRouteParams | adding to consolidated routing...", { name, value });
                destination[name] = value;
            };
        }
    }

    /**
     * Clears out all information for the principle, and redirects to a signout page.
     */
    @LogInvocation(LogLevel.DEBUG)
    logout(forceRedirect?: boolean): Promise<boolean>
    {
        const queryParams = this.buildRedirectParams(this.activatedRoute.snapshot);

        this.clearToken();

        if (this.$storageService.Get(SecurityStorageKey.TidToken))
        {
            return this.$openIdService.logout()
                .then(
                    () => true,
                    error =>
                    {
                        this.log.error(error);
                        return false;
                    });
        }

        return this.router.navigate(
            [SecurityRoute.Login],
            {
                queryParams,
            })
            .then((navigationSucceeded) =>
            {
                if (forceRedirect)
                {
                    this.location.reload();
                }

                return navigationSucceeded;
            });
    }

    /**
    * This will only clear out all information for the principle.
    * !!! This shouldn't be called out side of the library/core route guards and SecurityService's processes. !!!
     * @internal
    */
    @LogInvocation(LogLevel.DEBUG)
    clearToken(): void
    {
        if (!this.principle?.SaveCredentials)
        {
            this.forget(SecurityStorageKey.Username);
        }

        this.principle = undefined;

        this.forget(SecurityStorageKey.AccessToken);
        this.forget(SecurityStorageKey.SystemInfo);
        this.forget(SecurityStorageKey.DesiredRoute);
        this.forget(SecurityStorageKey.SearchParams);
        this.forget(SecurityStorageKey.RemindMeLater);

        this.window.sessionStorage.removeItem("erp-conngroupid");
        this.$openIdService.clearToken();
    }

    @LogInvocation(LogLevel.DEBUG)
    private redirectAfterLogin(loginViewModel: LoginView): void
    {
        if (this.isVendorUser)
        {
            this.redirectRoutingForVendor();
        }

        if (loginViewModel.RequirePasswordChangeOnLogin)
        {
            this.passwordResetRequired(loginViewModel.RequiredPasswordChangeKey);
            return;
        }

        try
        {
            this.returnToDesiredRoute();
        }
        catch (err)
        {
            this.router.navigate([""]);
        }
    }

    private forget(key: SecurityStorageKey): void
    {
        this.$storageService.Remove(key);
    }

    private get<T>(key: SecurityStorageKey): T
    {
        return this.$storageService.Get<T>(key);
    }

    private store<T>(key: SecurityStorageKey | SystemInfoStorageKeys, value: T): void
    {
        this.$storageService.Set(key, value);
    }
}

export class LoginError extends Error
{
    constructor(
        readonly response: HttpResponse<ExecutionResult<LoginView>>)
    {
        super(`LoginError: Could not login.`);
    }
}
